import {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { inject } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  finalize,
  first,
  switchMap,
  throwError,
} from 'rxjs';
import { CrmAuthApiService } from '../pages/auth/services/crm-auth-api.service';
import { CrmAuthCookieService } from '../pages/auth/services/crm-auth-cookie.service';
import { CrmAuthSessionService } from '../pages/auth/services/crm-auth-session.service';
import { environment } from '../../../../../libs/environment/environment';

/**
 * Tracks whether a token refresh is currently in progress.
 * While a refresh is inflight, all other 401-failing requests wait
 * for the refresh to complete and then retry with the new token.
 */
let refreshInProgress = false;
let refreshSubject = new BehaviorSubject<string | null>(null);

function isApiRequest(url: string, apiBaseUrl: string): boolean {
  return url.startsWith(apiBaseUrl);
}

const AUTH_EXCLUDED_ENDPOINTS = [
  '/v1/business/auth/login',
  '/v1/business/auth/verify-2fa',
  '/v1/business/auth/resend-2fa',
  '/v1/business/auth/refresh',
  '/v1/business/auth/logout',
  '/v1/business/auth/forgot-password',
  '/v1/business/auth/reset-password',
  '/v1/business/auth/set-password',
];

function isAuthExcludedRequest(url: string): boolean {
  const normalizedUrl = url.toLowerCase().split('?')[0];
  return AUTH_EXCLUDED_ENDPOINTS.some((endpoint) =>
    normalizedUrl.endsWith(endpoint),
  );
}

/**
 * Resets the shared refresh state after a refresh cycle completes
 * (whether success or failure), so subsequent 401s trigger a fresh
 * refresh flow.
 */
function finishRefresh(): void {
  refreshInProgress = false;
  refreshSubject = new BehaviorSubject<string | null>(null);
}

/**
 * Intercepts 401 responses, attempts token refresh, and retries the
 * original request with the new access token.
 *
 * This prevents the "tab hangs on loading" issue that occurs when:
 *  - User leaves a tab idle for some time
 *  - Access token expires
 *  - Browser throttles setInterval timers in background tabs
 *  - API requests silently fail with 401, leaving components stuck in loading state
 */
export const crmAuthErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const apiBaseUrl = environment.apiBaseUrl.replace(/\/$/, '');

  // Only intercept API requests
  if (!isApiRequest(req.url, apiBaseUrl)) {
    return next(req);
  }

  // Never intercept auth-specific requests to avoid infinite loops
  if (isAuthExcludedRequest(req.url)) {
    return next(req);
  }

  // Capture the session generation at the time the request is made.
  // If the user logs out and back in before this 401 arrives, the generation
  // will have changed and we skip logout to avoid disrupting the new session.
  const sessionService = inject(CrmAuthSessionService);
  const requestGeneration = sessionService.getSessionGeneration();

  return next(req).pipe(
    catchError(
      (error: unknown): Observable<HttpEvent<unknown>> => {
        if (!(error instanceof HttpErrorResponse)) {
          return throwError(() => error);
        }

        // Only handle 401
        if (error.status !== HttpStatusCode.Unauthorized) {
          return throwError(() => error);
        }

        // Check if we still have a valid refresh token
        const cookieService = inject(CrmAuthCookieService);
        const refreshToken = cookieService.getRefreshToken();
        const refreshTokenExpiresAt = cookieService.getRefreshTokenExpiresAt();

        // No refresh token, or it has already expired — skip refresh and logout
        if (!refreshToken || (refreshTokenExpiresAt !== null && refreshTokenExpiresAt <= Date.now())) {
          console.warn(
            '[AuthErrorInterceptor] No valid refresh token available.',
            { hasToken: !!refreshToken, expiresAt: refreshTokenExpiresAt ? new Date(refreshTokenExpiresAt).toISOString() : 'unknown' },
          );
          // Only logout if this 401 belongs to the current session.
          // A stale 401 from a previous session (after logout + re-login) must not
          // clobber the newly established session.
          if (requestGeneration === sessionService.getSessionGeneration()) {
            sessionService.logout();
          }
          return throwError(() => error);
        }

        // If a refresh is already in progress, wait for it and retry
        if (refreshInProgress) {
          return refreshSubject.pipe(
            filter((token): token is string => token !== null),
            first(),
            switchMap((newToken) => {
              return next(addAuthHeader(req, newToken));
            }),
          );
        }

        // Start a new refresh flow
        refreshInProgress = true;
        refreshSubject.next(null);

        const authApi = inject(CrmAuthApiService);

        return authApi.refreshToken({ refreshToken }).pipe(
          switchMap((res) => {
            if (res?.isSuccess || res?.success) {
              const data = res.data;
              const newAccessToken = data.accessToken;

              // Persist new tokens
              cookieService.setAuthCookies({
                accessToken: newAccessToken,
                refreshToken: data.refreshToken,
                accessTokenExpiresUtc: data.accessTokenExpiresUtc,
                refreshTokenExpiresUtc: data.refreshTokenExpiresUtc,
              });

              // Signal all waiting requests to retry
              refreshSubject.next(newAccessToken);

              // Retry the original request
              return next(addAuthHeader(req, newAccessToken));
            }

            // Refresh failed — trigger logout (generation check applies here too)
            if (requestGeneration === sessionService.getSessionGeneration()) {
              sessionService.logout();
            }
            return throwError(
              () =>
                new Error(
                  'Session expired. Please log in again.',
                ),
            );
          }),
          catchError((refreshError) => {
            // Refresh API call itself failed
            if (requestGeneration === sessionService.getSessionGeneration()) {
              sessionService.logout();
            }
            return throwError(() => refreshError);
          }),
          finalize(() => {
            // Reset shared refresh state so future 401s
            // trigger a fresh refresh cycle
            finishRefresh();
          }),
        );
      },
    ),
  );
};

function addAuthHeader(
  req: HttpRequest<unknown>,
  token: string,
): HttpRequest<unknown> {
  return req.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
  });
}